<template>
  <div>
    <CommonBreadCrumbs :data="breadcrumbs"></CommonBreadCrumbs>
    <div class="min-h-screen">
      <div class="w-full bg-[#F0FBFB] mt-3">
        <div class="-mt-4 md:mt-0">
          <div class="md:mt-5 relative flex flex-col md:flex-row w-full">
            <div
              class="tests-image w-full md:w-7/12 h-[200px] sm:h-[400px] bg-cover"
              alt=""
              :style="{
                backgroundImage: 'url(' + JSON.stringify(category.hero_banner?.permalink ?? '') + ')',
              }"
            />

            <div
              class="lg:w-3/6 lg:absolute -mt-10 mx-4 mb-10 lg:mt-0 lg:right-12 lg:top-8 md:bg-white rounded-lg md:shadow md:py-8 md:p-8 md:px-10"
            >
              <div class="flex flex-col">
                <div class="bg-white md:shadow-none rounded-lg shadow md:p-0 p-8">
                  <div class="flex gap-x-2 items-center">
                    <NuxtImg width="30px" height="30px" src="/icons/heartbeat.svg"></NuxtImg>
                    <span class="font-semibold">
                      {{ category.parent.title }}
                    </span>
                  </div>
                  <h1 class="lg:text-4xl text-[27px] font-semibold text-start leading-9 mb-4">
                    {{ $t(categoryTitleKey, { category: category.title }) }}
                  </h1>
                  <div class="flex base-1/2">
                    <BaseButton
                      class="text-sm font-normal px-8 bg-white h-[30px] mx-auto md:mx-0 md:mb-6 mb-0"
                      variant="outlined"
                      :text="
                        $t(categorySubTitleKey, {
                          category: category.title,
                        })
                      "
                      @click="scrollToView"
                    >
                      <NuxtImg width="8px" height="8px" src="/icons/arrow-down.svg"></NuxtImg>
                    </BaseButton>
                  </div>
                </div>
                <div class="leading-7 text-dark-blue tracking-[0.22px] font-semibold text-lg mt-8">
                  {{ category.intro_text }}
                </div>

                <NuxtLinkLocale
                  :locale="switchBlogLocale()"
                  @click="openServiceModal()"
                  class="underline cursor-pointer text-secondary my-4"
                >
                  {{ $t("tests.cardLink") }}
                </NuxtLinkLocale>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- How It Works Modal  -->
      <ProductInfoServiceModal :is-open="openModal" @on-modal-close="closeServiceModal" />

      <!-- FILTER SIDE BAR  -->
      <div v-if="filterSideBarOpen">
        <ProductMobileFilterSideBar
          :is-visible="filterSideBarOpen"
          :filters="contentFilters"
          @on-side-bar-close="closeFilterSideBar"
          :results-number="filtredProducts.length"
        />
      </div>
      <NuxtLayout name="center" id="tests" class="flex min-h-screen relative mt-10 md:mt-2 lg:pt-32">
        <!-- FILTER SIDEBAR OPTIONS  -->
        <NuxtLayout name="side-filters">
          <ProductFilterSidebar
            v-if="!filterSideBarOpen"
            :contentFilters="contentFilters"
            isActive
          ></ProductFilterSidebar>
        </NuxtLayout>
        <div class="w-full lg:ml-10 bg-white container" ref="scrollTarget">
          <div class="flex flex-col md:flex-row justify-between md:items-center">
            <ClientOnly>
              <span>{{ $t("tests.showing") }} {{ filtredProducts.length }} {{ $t("common.results") }}</span>
            </ClientOnly>
            <div class="flex items-center space-x-4">
              <FilterFloatButton
                @clicked="openFilterSideBar()"
                v-if="isVisible && !isCtaBlockVisible"
                :selectedFilters="selectedFilters"
              ></FilterFloatButton>

              <div class="relative">
                <BaseVSearchTextInput
                  id="search-product"
                  name="search-product"
                  :label="`${$t('filter.searchText')}`"
                  v-model="productSearch"
                />
                <button
                  class="flex justify-center items-center bg-secondary text-white md:w-8 md:h-8 w-[40px] h-[40px] rounded-md absolute right-1 top-5 md:top-1 z-10"
                >
                  <SearchIcon class="w-6 h-6" />
                </button>
              </div>
              <div class="relative hidden lg:flex">
                <BaseSelect :value="sortOptionSelected" :options="sortOptions" class="w-52" @onClick="setSortOption" />
              </div>
            </div>
          </div>
          <div ref="target" class="-mx-4">
            <ProductList :categoryParams="categoryParams"></ProductList>
          </div>
        </div>
      </NuxtLayout>

      <div class="w-full bg-[#F7F7F7]" v-if="category.body_text">
        <NuxtLayout name="clinic" class="py-12 md:px-0 flex md:flex-row flex-col md:gap-x-32 md:w-full">
          <CommonLiveChatBlock mobile class="w-full mt-8 order-last md:order-first" />
          <div v-html="category.body_text" class="test-content"></div>
        </NuxtLayout>
      </div>
      <NuxtLayout name="center">
        <ProductSubCategory
          :parentCategory="category.parent"
          :subTitle="`${$t('filter.subCatSubTitle')}`"
        ></ProductSubCategory>
      </NuxtLayout>

      <CTABlock imgSrc="USPs_graphic_03_1" get-started ref="ctaBlock" />
    </div>
  </div>
</template>
<script setup>
import { useProductStore } from "@/stores/filter";
import { SearchIcon } from "@heroicons/vue/outline";
import CTABlock from "~~/components/clinic/CTABlock.vue";
const route = useRoute();
const category = ref({});
const bodyText = ref([]);
const currentCategory = route.params.slug;
const target = ref(null);
const categoryParams = ref({});
const ctaBlock = ref(null);
const { isCtaBlockVisible, isVisible } = useCheckVisibility(target, ctaBlock);
category.value = await useFetchCategoryDetails(currentCategory, route.path.split("/")[1]);

// Guard against invalid intermediate category slugs (e.g. /tests/big-hroses/<subcategory>).
// The URL segment must match the subcategory's actual parent category.
const requestedParentSlug = String(route.params.id ?? "").toLowerCase();
if (requestedParentSlug !== String(category.value?.parent?.slug ?? "").toLowerCase()) {
  throw createError({ fatal: true, statusCode: 404, statusMessage: "Page Not Found" });
}

categoryParams.value = { "categories[0]": category.value.id };
const filterStore = useProductStore();
const contentFilters = ref([]);
const slicedIndex = ref(12);
const filterSideBarOpen = ref(false);

contentFilters.value = await useFilterOptions(categoryParams.value);
filterStore.getFilterOptions(contentFilters.value.min_price, contentFilters.value.max_price);

onMounted(() => {
  //filterStore.clearAllFilters()
  filterStore.setSortOption(2);
  const regex = /<p>(.*?)<\/p>/g;
  bodyText.value = category.value.body_text.match(regex);
  selectedFiltersArray();
});

const selectedFilters = computed({
  get() {
    return filterStore.getCheckedFilter;
  },
});

const sortOptions = computed(() => filterStore.getSortOptions);
const sortOptionSelected = computed(() => filterStore.selectedSortOption);
const productSearch = computed({
  get() {
    if (filterStore.search == "") {
      filterStore.setSortOption(2);
    } else {
      filterStore.setSortOption(0);
    }
    return filterStore.search;
  },
  set(val) {
    filterStore.setSearchValue(val);
  },
});

const filtredProducts = computed(() => filterStore.filtredTests);

const isHomeTestsCategory = computed(() => {
  const slug = category.value?.slug ?? "";
  const parentSlug = category.value?.parent?.slug ?? "";
  return slug === "home-tests" || slug === "home_test" || parentSlug === "home-tests" || parentSlug === "home_test";
});

const categoryTitleKey = computed(() =>
  isHomeTestsCategory.value ? "tests.category.titleNoSuffix" : "tests.category.title"
);
const categorySubTitleKey = computed(() =>
  isHomeTestsCategory.value ? "tests.category.subTitleNoSuffix" : "tests.category.subTitle"
);

const setSortOption = (item) => {
  filterStore.setSortOptionMobile(item);
};

const openFilterSideBar = () => {
  document.body.classList.toggle("overflow-hidden");
  filterSideBarOpen.value = true;
};
const closeFilterSideBar = () => {
  document.body.classList.toggle("overflow-hidden");
  filterSideBarOpen.value = false;
};

const selectedFiltersArray = () => {
  return filterStore.checked;
};
const scrollTarget = ref(null);
const scrollToView = (e) => {
  e.preventDefault();
  // Scroll to a certain element
  scrollTarget.value.scrollIntoView({
    behavior: "smooth",
  });
};

const openModal = ref(false);

const openServiceModal = () => {
  openModal.value = true;
};
const closeServiceModal = () => {
  openModal.value = false;
};

useMetaTitles(category.value, "test-category");

const breadcrumbs = getBreadcrumbs("test-category", category.value);
</script>

<style scoped>
html {
  scroll-behavior: smooth;
}

.target1 {
  border: 2px dashed;
  padding: 10px;
  max-height: 150px;
  margin: 0 2rem 400px;
}
</style>
